import { createFileRoute, useRouter } from '@tanstack/react-router'
import { withMask } from 'use-mask-input'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Input } from '@/components/ui/input'
import { CheckBox } from '@/components/ui/check-box'
import { Controller, useForm } from 'react-hook-form'

import styles from './styles.module.scss'
import { useCallback } from 'react'
import { userServiceInstance } from '@/services/user.service'
import { useAtom } from 'jotai'
import { userAtom } from '@/store/atoms/user.atom'

export const Route = createFileRoute('/desktop/')({
  component: RouteComponent,
})

const validarCPF = (cpf: string) => {
  cpf = cpf.replace(/\D/g, ""); // Remove caracteres não numéricos

  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
    return false; // CPF com números repetidos ou com menos de 11 dígitos
  }

  let soma = 0;
  let resto;

  // Validação do primeiro dígito verificador
  for (let i = 1; i <= 9; i++) {
    soma += parseInt(cpf[i - 1]) * (11 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf[9])) return false;

  soma = 0;
  // Validação do segundo dígito verificador
  for (let i = 1; i <= 10; i++) {
    soma += parseInt(cpf[i - 1]) * (12 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf[10])) return false;

  return true;
};
const formSchema = z.object({
  username: z.string({ error: 'Nome inválido' }),
  email: z.email({ error: 'E-mail inválido' }),
  phoneNumber: z.string({ error: 'Telefone inválido' }).refine(phone => phone.startsWith("(85)") || phone.startsWith("(88)"), { message: 'Telefone inválido' }).transform((value) => value.replace(/[^0-9]/g, "")),
  cpf: z.string().min(14, "CPF inválido")
    .regex(
      /^(?!000\.000\.000\-00)(?!111\.111\.111\-11)(?!222\.222\.222\-22)(?!333\.333\.333\-33)(?!444\.444\.444\-44)(?!555\.555\.555\-55)(?!666\.666\.666\-66)(?!777\.777\.777\-77)(?!888\.888\.888\-88)(?!999\.999\.999\-99)\d{3}\.\d{3}\.\d{3}\-\d{2}$/,
      "CPF inválido"
    )
    .refine((value) => validarCPF(value), "CPF inválido")
    .transform((value) => value.replace(/[^0-9]/g, "")),
  terms: z.boolean()
})

type FormSchema = z.infer<typeof formSchema>

function RouteComponent() {
  const router = useRouter()
  const [, setUser] = useAtom(userAtom)
  const { control, handleSubmit, watch } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    mode: "all"
  })

  const acceptTerms = watch('terms')

  const onSubmit = useCallback(async (data: FormSchema) => {
    const user = await userServiceInstance.login(data)
    setUser(user)
    router.navigate({
      to: '/desktop/home'
    })
  }, [])

  return (
    <div className={styles.container}>
      <div>
        <div>
          <img
            src="/assets/login_desk.png"
          />
        </div>
        <div className={styles.form_container}>
          <header>
            <h2>Cadastro</h2>
          </header>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              name="username"
              render={({ field, fieldState: { error } }) => (
                <Input
                  label='Nome'
                  placeholder='Ex: João da Silva'
                  errorMessage={error?.message}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              name="cpf"
              render={({ field, fieldState: { error } }) => (
                <Input
                  label='CPF'
                  placeholder='Ex: 999.999.999-99'
                  {...field}
                  //@ts-expect-error 
                  ref={withMask('999.999.999-99')}
                  errorMessage={error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field, fieldState: { error } }) => (
                <Input
                  label='Telefone'
                  {...field}
                  placeholder='Ex: (99) 99999-9999'
                  //@ts-expect-error
                  ref={withMask('(99) 99999-9999')}
                  errorMessage={error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <Input
                  label='E-mail'
                  placeholder='Ex: exemplo@exemplo.com'
                  errorMessage={error?.message}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              name="terms"
              render={({ field: { value, onChange } }) => (
                <CheckBox
                  onChange={onChange}
                  checked={value}
                  label={
                    <span>
                      Eu li e concordo com os <a href="https://lotece.com.br/politica-de-privacide-sortelec-digital/" target="_blank">termos de uso</a>
                    </span>
                  }
                />
              )}
            />

            <footer>
              <button
                disabled={!acceptTerms}
              >
                <img
                  src="/assets/login_btn.png"
                />
              </button>
            </footer>
          </form>
        </div>
      </div>
    </div>
  )
}
